import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import DataPageLayout from '../components/DataPageLayout';
import ImportPreviewModal from '../components/ImportPreviewModal';
import {
  addEmail,
  bulkAddEmails,
  deleteEmails,
  listEmails,
  updateEmail,
  verifyImportEmails,
} from '../services/emailCredentialService';
import './Dashboard.css';

const SEND_LIMIT = 500;
const WINDOW_HOURS = 24;
const MAX_IMPORT_FILE_SIZE = 10 * 1024 * 1024;

function getAvailableQuota(entry) {
  return entry.available ?? Math.max(0, SEND_LIMIT - (entry.used ?? 0));
}

function getUsedQuota(entry) {
  return entry.used ?? 0;
}

function getSentInWindow(entry) {
  return entry.sentInWindow ?? 0;
}

function getPendingQuota(entry) {
  return entry.pending ?? 0;
}

function formatCount(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  return Number(value).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString();
}

function splitCsvLine(line) {
  const cells = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells.map((cell) => cell.replace(/^"|"$/g, ''));
}

/** Parse CSV for pending table rows + plaintext passwords (masked in UI). */
function parseImportCsv(text) {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return {
      rows: [],
      secretsByEmail: new Map(),
      error: 'CSV file has no data rows',
    };
  }

  const headers = splitCsvLine(lines[0]).map((header) => header.toLowerCase());
  const emailIndex = headers.indexOf('email');
  const passwordIndex = headers.indexOf('password');
  const providerIndex = headers.indexOf('provider');

  if (emailIndex === -1 || passwordIndex === -1 || providerIndex === -1) {
    return {
      rows: [],
      secretsByEmail: new Map(),
      error: 'CSV must include columns: email, password, provider, display_name',
    };
  }

  let displayNameIndex = headers.indexOf('display_name');
  if (displayNameIndex === -1) {
    displayNameIndex = headers.indexOf('displayname');
  }

  if (displayNameIndex === -1) {
    return {
      rows: [],
      secretsByEmail: new Map(),
      error: 'CSV must include columns: email, password, provider, display_name',
    };
  }

  const rows = [];
  const secretsByEmail = new Map();

  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i]);
    const email = (cells[emailIndex] || '').trim();
    const password = normalizePassword(cells[passwordIndex] || '');
    const provider = (cells[providerIndex] || '').trim().toLowerCase();
    const displayName = normalizeDisplayName(cells[displayNameIndex] || '');

    if (!email && !password && !provider && !displayName) {
      continue;
    }

    if (email && password && provider && displayName && !secretsByEmail.has(email)) {
      secretsByEmail.set(email, { password, provider, displayName });
    }

    rows.push({
      row: i + 1,
      email,
      provider,
      display_name: displayName,
      password: password ? '********' : '',
      status: 'pending',
    });
  }

  return { rows, secretsByEmail };
}

function summarizeImportRows(rows) {
  return {
    total: rows.length,
    success: rows.filter((row) => row.status === 'success' || row.status === 'new').length,
    exists: rows.filter((row) => row.status === 'exists').length,
    failed: rows.filter(
      (row) => row.status === 'failed' || row.status === 'invalid'
    ).length,
    pending: rows.filter(
      (row) => row.status === 'pending' || row.status === 'verifying'
    ).length,
    rows,
  };
}

/** Gmail app passwords are often copied with spaces — strip before verify/save. */
function normalizePassword(password) {
  return String(password || '').replace(/\s+/g, '');
}

function normalizeDisplayName(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ');
}

function Emails() {
  const fileInputRef = useRef(null);
  const importSecretsRef = useRef(new Map());
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [error, setError] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listEmails();
      setEmails(data);
      setError('');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const totalUsed = useMemo(
    () => emails.reduce((sum, entry) => sum + getUsedQuota(entry), 0),
    [emails]
  );

  const totalRemaining = useMemo(
    () => emails.reduce((sum, entry) => sum + getAvailableQuota(entry), 0),
    [emails]
  );

  const activeCredentials = useMemo(
    () => emails.filter((entry) => getAvailableQuota(entry) > 0).length,
    [emails]
  );

  const metrics = [
    {
      title: 'Total senders',
      value: loading ? '...' : formatCount(emails.length),
      subtext: 'Sender email accounts',
      highlight: true,
    },
    {
      title: 'Sends left',
      value: loading ? '...' : formatCount(totalRemaining),
      subtext: `Across all senders in the last ${WINDOW_HOURS} hours`,
    },
    {
      title: 'Sends used',
      value: loading ? '...' : formatCount(totalUsed),
      subtext: `Sent in the last ${WINDOW_HOURS} hours, plus ones waiting`,
    },
    {
      title: 'Ready to send',
      value: loading ? '...' : formatCount(activeCredentials),
      subtext: `Still has sends left in the last ${WINDOW_HOURS} hours`,
      highlight: true,
    },
  ];

  const handleImportClick = () => {
    setImportMessage('');
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files are supported.');
      return;
    }

    if (file.size > MAX_IMPORT_FILE_SIZE) {
      toast.error('File is too large. Maximum size is 10MB.');
      return;
    }

    setImportLoading(true);
    setImportMessage('');
    setError('');
    setPreviewLoading(true);
    setPendingImportFile(file);
    importSecretsRef.current = new Map();

    try {
      const text = await file.text();
      const { rows: pendingRows, secretsByEmail, error: parseError } =
        parseImportCsv(text);
      importSecretsRef.current = secretsByEmail;

      if (parseError) {
        throw new Error(parseError);
      }

      if (!pendingRows.length) {
        throw new Error('CSV file has no data rows');
      }

      setImportPreview(summarizeImportRows(pendingRows));

      const preview = await verifyImportEmails(file);
      setImportPreview({
        ...preview,
        success: preview.success ?? preview.new ?? 0,
        failed: preview.failed ?? preview.invalid ?? 0,
        pending: 0,
      });
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
      setPendingImportFile(null);
      setImportPreview(null);
      importSecretsRef.current = new Map();
    } finally {
      setImportLoading(false);
      setPreviewLoading(false);
    }
  };

  const handleCloseImportPreview = () => {
    if (confirmingImport || previewLoading) {
      return;
    }

    setPendingImportFile(null);
    setImportPreview(null);
    setPreviewLoading(false);
    importSecretsRef.current = new Map();
  };

  const handleConfirmImport = async () => {
    const successRows = (importPreview?.rows || []).filter(
      (row) => row.status === 'success' || row.status === 'new'
    );

    const emailsPayload = successRows
      .map((row) => {
        const secrets = importSecretsRef.current.get(row.email) || {};
        return {
          row: row.row,
          email: row.email,
          provider: row.provider || secrets.provider || '',
          password: secrets.password || '',
          displayName:
            secrets.displayName ||
            row.display_name ||
            row.displayName ||
            '',
        };
      })
      .filter((row) => row.email && row.password && row.provider && row.displayName);

    if (!emailsPayload.length) {
      toast.error('No verified emails to add.');
      return;
    }

    setConfirmingImport(true);
    setError('');

    try {
      const result = await bulkAddEmails(emailsPayload);
      const inserted = result.success ?? result.inserted ?? 0;
      const existsCount = result.exists ?? 0;
      const failedCount = result.failed ?? 0;
      const message = `Import finished: ${inserted} success, ${existsCount} already exists, ${failedCount} failed.`;

      setImportMessage(message);
      setPendingImportFile(null);
      setImportPreview(null);
      importSecretsRef.current = new Map();
      await loadEmails();

      if (failedCount > 0 || existsCount > 0) {
        toast.info(message);
      } else {
        toast.success(message);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setConfirmingImport(false);
    }
  };

  const importPreviewColumns = [
    { key: 'email', label: 'Email' },
    { key: 'display_name', label: 'Display name' },
    { key: 'provider', label: 'Provider' },
    { key: 'password', label: 'Password' },
  ];

  const columns = [
    { key: 'id', label: 'ID' },
    {
      key: 'email',
      label: 'Email',
      minWidth: '280px',
      grow: 2,
    },
    {
      key: 'display_name',
      label: 'Display name',
      minWidth: '180px',
      grow: 1,
      render: (row) => row.display_name || row.displayName || '—',
    },
    {
      key: 'provider',
      label: 'Provider',
      minWidth: '110px',
      render: (row) => row.provider || '—',
    },
    {
      key: 'sentInWindow',
      label: `Sent (${WINDOW_HOURS}h)`,
      render: (row) => formatCount(getSentInWindow(row)),
    },
    {
      key: 'pending',
      label: 'Pending',
      render: (row) => formatCount(getPendingQuota(row)),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        const isActive = getAvailableQuota(row) > 0;
        return (
          <span className={`data-page__status data-page__status--${isActive ? 'active' : 'full'}`}>
            {isActive ? 'active' : 'full'}
          </span>
        );
      },
    },
    {
      key: 'remaining',
      label: 'Available',
      render: (row) => formatCount(getAvailableQuota(row)),
    },
    {
      key: 'created_at',
      label: 'Added At',
      render: (row) => formatDate(row.created_at),
    },
  ];

  const providerField = {
    key: 'provider',
    label: 'Provider',
    type: 'select',
    placeholder: 'Select provider',
    options: [
      { value: 'gmail', label: 'Gmail' },
      { value: 'godaddy', label: 'GoDaddy' },
    ],
  };

  const displayNameField = {
    key: 'display_name',
    label: 'Display name',
    type: 'text',
    placeholder: 'Shown as the From name',
  };

  const emailAddFields = [
    providerField,
    displayNameField,
    { key: 'email', label: 'Email', type: 'email', validate: 'email' },
    { key: 'password', label: 'Password', type: 'password' },
  ];

  const emailEditFields = [
    providerField,
    displayNameField,
    { key: 'email', label: 'Email', type: 'email', validate: 'email' },
    {
      key: 'password',
      label: 'Password',
      type: 'password',
      required: false,
      maskedValue: '********',
      placeholder: 'Leave blank to keep current password',
    },
  ];

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleImportFile}
        hidden
      />
      <DataPageLayout
        title="Emails"
        subtitle="Manage sender email accounts and how many messages they can still send."
        metrics={metrics}
        loading={loading}
        error={error}
        successMessage={importMessage}
        onRefresh={loadEmails}
        importLabel="Import Emails"
        onImportClick={handleImportClick}
        importLoading={importLoading}
        addLabel="Add Email"
        addSavingLabel="Checking email..."
        editSavingLabel="Checking email..."
        addFields={emailAddFields}
        isAddSubmitEnabled={(values) => {
          const provider = String(values.provider || '').trim();
          const email = String(values.email || '').trim();
          const password = normalizePassword(values.password);
          const displayName = normalizeDisplayName(values.display_name);
          return Boolean(provider && email && password && displayName);
        }}
        onSaveAdd={async (values) => {
          try {
            await addEmail({
              provider: String(values.provider || '').trim(),
              email: values.email.trim(),
              password: normalizePassword(values.password),
              displayName: normalizeDisplayName(values.display_name),
            });
            setError('');
            setImportMessage('Sender email added.');
            await loadEmails();
            toast.success('Sender email added.');
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        columns={columns}
        rows={emails}
        searchable
        searchPlaceholder="Search email, name, or provider..."
        searchKeys={['email', 'display_name', 'displayName', 'provider']}
        emptyMessage="No sender emails yet."
        editFields={emailEditFields}
        isEditSubmitEnabled={(values, initialValues) => {
          const nextProvider = String(values.provider || '').trim();
          const initialProvider = String(initialValues.provider || '').trim();
          const nextEmail = String(values.email || '').trim();
          const initialEmail = String(initialValues.email || '').trim();
          const nextDisplayName = normalizeDisplayName(values.display_name);
          const initialDisplayName = normalizeDisplayName(
            initialValues.display_name || initialValues.displayName
          );
          const providerChanged = nextProvider && nextProvider !== initialProvider;
          const emailChanged = nextEmail !== initialEmail;
          const passwordChanged = Boolean(normalizePassword(values.password));
          const displayNameChanged =
            nextDisplayName && nextDisplayName !== initialDisplayName;

          if (!nextProvider || !nextEmail || !nextDisplayName) {
            return false;
          }

          // Password required when email changes; otherwise allow provider/password/display name updates
          if (emailChanged) {
            return passwordChanged;
          }

          return providerChanged || passwordChanged || displayNameChanged;
        }}
        onSaveEdit={async (row, values) => {
          try {
            const nextProvider = String(values.provider || '').trim();
            const nextEmail = values.email.trim();
            const nextPassword = values.password
              ? normalizePassword(values.password)
              : '';
            const nextDisplayName = normalizeDisplayName(values.display_name);

            if (!nextProvider) {
              throw new Error('Provider is required.');
            }

            if (!nextDisplayName) {
              throw new Error('Display name is required.');
            }

            if (nextEmail !== row.email && !nextPassword) {
              throw new Error('Enter the app password when changing the email address.');
            }

            const payload = {
              provider: nextProvider,
              email: nextEmail,
              displayName: nextDisplayName,
            };
            if (nextPassword) {
              payload.password = nextPassword;
            }

            await updateEmail(row.id, payload);
            setError('');
            setImportMessage('Sender email updated.');
            await loadEmails();
            toast.success('Sender email updated.');
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        onDelete={async (row) => {
          try {
            await deleteEmails([row.id]);
            await loadEmails();
            setError('');
            toast.success('Sender email deleted.');
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        onDeleteMany={async (selected) => {
          try {
            await deleteEmails(selected.map((row) => row.id));
            await loadEmails();
            setError('');
            toast.success(`Deleted ${selected.length} sender email(s).`);
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        onDeleteAll={async (allRows) => {
          try {
            await deleteEmails(allRows.map((row) => row.id));
            await loadEmails();
            setError('');
            toast.success(`Deleted all ${allRows.length} sender emails.`);
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        deleteConfirmMessage="Delete this sender email?"
        deleteManyConfirmMessage="Delete the selected sender emails?"
        deleteAllConfirmMessage="Delete all sender emails?"
      />

      {importPreview && (
        <ImportPreviewModal
          title="Import Emails Preview"
          fileName={pendingImportFile?.name}
          columns={importPreviewColumns}
          rows={importPreview.rows}
          total={importPreview.total}
          successCount={importPreview.success ?? importPreview.new}
          existsCount={importPreview.exists}
          failedCount={importPreview.failed ?? importPreview.invalid}
          pendingCount={importPreview.pending}
          loading={previewLoading}
          confirming={confirmingImport}
          onClose={handleCloseImportPreview}
          onConfirm={handleConfirmImport}
          loadingMessage="Checking emails..."
          successLabel="success"
          existsLabel="already exists"
          failedLabel="failed"
          confirmLabel={`Add ${importPreview.success ?? importPreview.new ?? 0} checked email(s)`}
        />
      )}
    </>
  );
}

export default Emails;
